import { useCallback, useEffect, useMemo } from "react"
import { Routes, Route } from "react-router-dom"
import _ from "lodash"
import { useIdleTimer } from "react-idle-timer"
import { useNavigate } from "react-router-dom"
import { AnimatePresence } from "framer-motion"
import config from "config"
import {
  DEVICE_KIOSK_V,
  KIOSK_CONFIG_NAME_KEY,
  VENUE_DEVICE_NAME_KEY,
} from "constant"
import i18n from "./i18n"

import {
  set as setPersistValue,
  get as getPersistValue,
} from "utils/persist_store"
import { homeScreenMode, updateAppStatus, APP_STATUS } from "utils/electron"

import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"

import { useSession } from "hooks/analytics/useSession"
import { useUptimeHeartbeat } from "hooks/useUptimeHeartbeat"

// Extensions
import { extensionConfigs } from "extensions"
import {
  getAllExtensionRoutes,
  getExtensionAppComponentConfig,
  getExtensionAppLayoutConfig,
  getExtensionThemeFactories,
  mergeRoutes,
  registerRouteByConfig,
} from "utils/extension"

// Theme
import {
  generateTheme,
  appThemeOptionFactory as directoryThemeOptionFactory,
} from "theme"
import { ThemeProvider } from "providers/mui-extended/ThemeProvider"

// Routes
import { standardRoutes } from "standard"

// Pages
import { Layout as MapLayout } from "pages/maps/_layout.kiosk-v"

// Components, Layouts
import { AppLayout, PageLayout } from "layouts/kiosk-v"
import { ScrollToTop } from "components/ScrollToTop"
import { LoadingScreen, MapUpdatingScreen } from "components/LoadingScreen"
import { MotionDiv, showContent } from "components/motions"
import { createIndexRoute } from "providers/route/utils/createIndexRoute"
import { MapIndex } from "pages/maps"
import { createTheme } from "@mui/material"
import { ChildPlayground, ParentPlayground } from "iframe-bridge/playground"
import { BridgeProvider } from "iframe-bridge-react"
import { createParentHandlers } from "iframe-bridge"

const resetEvent = new Event("appreset")

// Extension routes
const extensionRoutes = getAllExtensionRoutes(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)

// Merge Standard + Extension routes
const { pageRoutes, mapRoutes, baseRoutes } = mergeRoutes(
  _.get(standardRoutes, DEVICE_KIOSK_V),
  extensionRoutes
)

// Extension Themes
const extensionThemeConfig = getExtensionThemeFactories(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)

// Extension App Layout Config
const result = getExtensionAppLayoutConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)
const { search, appNavigation } = result

// Extension App Components Config
const appComponentConfig = getExtensionAppComponentConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_KIOSK_V
)
const { loadingScreen, preservedView } = appComponentConfig

// App Theme Factory
const appThemeOptionFactory = _.get(directoryThemeOptionFactory, DEVICE_KIOSK_V)

export const KioskVApp = () => {
  // Generate a venue theme
  const {
    themeOptions: venueThemeOptions,
    // refetchImdfData,
    dataLoaded,
    indexPath,
    idleModeConfig,
    iframeBridgeConfig,
  } = useVenue()
  const { redirectPath, ...useIdleTimerProps } = idleModeConfig
  const { enabled, iframeOrigin, iframeUrl, readyTimeout, diagnosticLabels } =
    iframeBridgeConfig
  const {
    INITIAL_SESSION_KEY,
    gtmPushDataLayerCountKioskUser,
    gtmPushSurveyNewSession,
  } = useSession()

  // Periodically ping the uptime monitor so the kiosk is reported as online.
  useUptimeHeartbeat()

  const {
    mapReady,
    mapLoading,
    setWheelchairModeEnabled,
    updateRouteOptions,
    themeDirection,
    isIdleTimerPaused,
  } = useAppUI()
  const theme = generateTheme(venueThemeOptions, appThemeOptionFactory, {
    extendThemeFactories: extensionThemeConfig.themeFactories,
    ignoreAppThemeFactory: extensionThemeConfig.ignoreAppThemeFactory,
    ignoreDeviceThemeFactory: extensionThemeConfig.ignoreDeviceThemeFactory,
  })
  const navigate = useNavigate()
  const isAppReady = useMemo(
    () => mapReady && dataLoaded,
    [mapReady, dataLoaded]
  )
  const onIdle = useCallback(async () => {
    setWheelchairModeEnabled(false)
    window.dispatchEvent(resetEvent)
    gtmPushSurveyNewSession()
    homeScreenMode()
    updateAppStatus(APP_STATUS.IDLE)
    // navigate last: dispatchEvent runs "appreset" listeners synchronously,
    // so the redirect must come after them to avoid being raced/overridden.
    // e.g. if navigate() ran first:
    //   1. navigate(redirectPath) -> schedules navigation to home
    //   2. dispatchEvent("appreset") -> some listener also calls navigate(x)
    //   3. listener's navigate(x) wins -> kiosk ends up on x, not home
    navigate(redirectPath)
    // Refetch IMDF data on idle
    // await refetchImdfData()
  }, [
    redirectPath,
    setWheelchairModeEnabled,
    gtmPushSurveyNewSession,
    navigate /* , refetchImdfData */,
  ])

  const onActive = useCallback(() => {
    gtmPushDataLayerCountKioskUser()
  }, [gtmPushDataLayerCountKioskUser])

  const onAction = useCallback(() => {
    if (!getPersistValue(INITIAL_SESSION_KEY)) {
      gtmPushDataLayerCountKioskUser()
    }
    setPersistValue(INITIAL_SESSION_KEY, true)
  }, [INITIAL_SESSION_KEY, gtmPushDataLayerCountKioskUser])

  useIdleTimer({
    onActive,
    onAction,
    onIdle,
    timeout: 1000 * 60 * 5,
    throttle: 500,
    ...useIdleTimerProps,
    disabled: Boolean(useIdleTimerProps.disabled) || isIdleTimerPaused,
  })

  useEffect(() => {
    updateRouteOptions({ baseRoutes, pageRoutes, mapRoutes })
  }, [updateRouteOptions])

  const themeWithDirection = useMemo(
    () =>
      createTheme(theme, {
        direction: themeDirection,
        isRtl: themeDirection === "rtl",
      }),
    [theme, themeDirection]
  )

  const metaGetter = useCallback(() => {
    return {
      lang: i18n.resolvedLanguage || i18n.language,
      deviceName:
        getPersistValue(VENUE_DEVICE_NAME_KEY) ||
        getPersistValue(KIOSK_CONFIG_NAME_KEY),
    }
  }, [])

  const bridgeEventHandlers = useMemo(
    () =>
      createParentHandlers({
        openSearch: ({ q }) => {
          if (!q) return
          navigate(`?search=1&q=${q}`, { replace: true })
        },
        openPlace: ({ id }) => {
          if (!id) return
          navigate(`/maps/place/${id}`, { replace: true })
        },
        openServices: () => {
          navigate(`/services`, { replace: true })
        },
        openEvents: () => {
          navigate(`/events`, { replace: true })
        },
        openEventDetail: ({ id }) => {
          if (!id) return
          navigate(`/events/${id}`, { replace: true })
        },
        openPromotions: () => {
          navigate(`/promotions`, { replace: true })
        },
        openPromotionDetail: ({ id }) => {
          if (!id) return
          navigate(`/promotions/${id}`, { replace: true })
        },
        openDirectory: () => {
          navigate(`/directory`, { replace: true })
        },
        showDirections: ({ from, to }) => {
          if (!to) return
          navigate(`/maps/dir/${from || "unknown"}/${to}`, { replace: true })
        },
        startNavigation: ({ from, to }) => {
          if (!to) return
          navigate(`/maps/dir/${from || "unknown"}/${to}/navigate/steps/1`, {
            replace: true,
          })
        },
        changeStepNavigation: ({ from, to, step = 1 }) => {
          if (!to) return
          navigate(
            `/maps/dir/${from || "unknown"}/${to}/navigate/steps/${step}`
          )
        },
      }),
    [navigate]
  )

  const disconnectHandler = useCallback(() => {
    navigate(`?ai=0`, { replace: true })
  }, [navigate])

  return (
    <BridgeProvider
      enabled={enabled}
      iframeOrigin={iframeOrigin}
      iframeUrl={iframeUrl}
      readyTimeoutMs={readyTimeout}
      diagnosticLabels={diagnosticLabels}
      metaGetter={metaGetter}
      onEvent={bridgeEventHandlers}
      onDisconnect={disconnectHandler}
    >
      <ThemeProvider theme={themeWithDirection}>
        <ScrollToTop />
        {isAppReady ? preservedView : null}
        <AnimatePresence mode="sync">
          {!isAppReady
            ? loadingScreen || <LoadingScreen key="loading-screen" />
            : null}
          <MapUpdatingScreen key="map-updating" open={mapLoading} />
          <MotionDiv
            key="content"
            variants={showContent}
            initial="initial"
            animate="animate"
          >
            <Routes>
              <Route
                path="/"
                element={
                  <AppLayout search={search} appNavigation={appNavigation} />
                }
              >
                {createIndexRoute({ to: indexPath })}
                {baseRoutes.map(registerRouteByConfig)}
                <Route path="/*" element={<PageLayout />}>
                  {/* Add or override specific route extension pages here. */}
                  {pageRoutes.map(registerRouteByConfig)}
                </Route>

                <Route path="/maps/*" element={<MapLayout />}>
                  <Route index element={<MapIndex />} />
                  {/* The indoorPage parent router treats the route child parameter as the same parameter as its own. */}
                  {mapRoutes.map(registerRouteByConfig)}
                </Route>
                <Route
                  path="/iframe-bridge/playground/parent"
                  element={<ParentPlayground />}
                />
                <Route
                  path="/iframe-bridge/playground/child"
                  element={<ChildPlayground />}
                />
              </Route>
            </Routes>
          </MotionDiv>
        </AnimatePresence>
      </ThemeProvider>
    </BridgeProvider>
  )
}
